import { useEffect, useMemo, useRef } from 'react';
import { usePathParam, useQueryParam } from '@atlassian/react-resource-router';
import {
	BOARD_SETTINGS_CONFIG_QUERY_PARAM_VALUES,
	type ConfigParamKey,
} from '../../common/constants.tsx';

export function useConfigParam(): ConfigParamKey | null {
	const lastValidConfig = useRef<ConfigParamKey | null>(null);
	const configParam = useMaybeConfigParam();

	const result = useMemo(() => {
		if (!configParam) {
			if (lastValidConfig.current) {
				return lastValidConfig.current;
			}
			throw new Error('No config param found');
		}

		return isConfigQuery(configParam) ? configParam : null;
	}, [configParam]);

	useEffect(() => {
		if (result) {
			lastValidConfig.current = result;
		}
	}, [result]);

	return result;
}

// This is the weakly-typed version, useful when the param CAN be undefined.
export function useMaybeConfigParam() {
	const [configPath] = usePathParam('config');
	const [configQuery] = useQueryParam('config');

	return configPath || configQuery;
}

const isConfigQuery = (configQuery: string): configQuery is ConfigParamKey =>
	BOARD_SETTINGS_CONFIG_QUERY_PARAM_VALUES.has(configQuery);
